import React, { useMemo } from 'react'
import { ButtonProps } from './types'

/**
 * Componente Button minimalista con diseño limpio y consistente
 * Soporta variantes, tamaños, estados de carga e iconos Heroicons
 */
const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  iconClassName,
  className = '',
  fullWidth = false,
  outline = false,
  disabled,
  children,
  ...rest
}) => {
  const isDisabled = disabled || isLoading

  // Clases de tamaño - minimalista
  const sizeClasses = useMemo(() => {
    switch (size) {
      case 'sm':
        return 'px-3 py-1.5 text-sm'
      case 'lg':
        return 'px-5 py-3 text-base'
      default:
        return 'px-4 py-2 text-sm'
    }
  }, [size])

  // Clases de variante - minimalista
  const variantClasses = useMemo(() => {
    if (outline) {
      // Variantes outline (solo borde)
      switch (variant) {
        case 'primary':
          return 'border-blue-600 text-blue-600 hover:bg-blue-50 focus:ring-blue-500'
        case 'secondary':
          return 'border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500'
        case 'danger':
          return 'border-red-600 text-red-600 hover:bg-red-50 focus:ring-red-500'
        case 'success':
          return 'border-green-600 text-green-600 hover:bg-green-50 focus:ring-green-500'
        case 'warning':
          return 'border-yellow-600 text-yellow-600 hover:bg-yellow-50 focus:ring-yellow-500'
        case 'ghost':
          return 'border-transparent text-gray-700 hover:bg-gray-100 focus:ring-gray-500'
        default:
          return 'border-blue-600 text-blue-600 hover:bg-blue-50 focus:ring-blue-500'
      }
    } else {
      // Variantes sólidas
      switch (variant) {
        case 'primary':
          return 'bg-blue-800 text-white hover:bg-blue-900 focus:ring-blue-500'
        case 'secondary':
          return 'bg-gray-200 text-gray-700 hover:bg-gray-300 focus:ring-gray-500'
        case 'danger':
          return 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500'
        case 'success':
          return 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
        case 'warning':
          return 'bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-yellow-500'
        case 'ghost':
          return 'bg-transparent text-gray-700 hover:bg-gray-100 focus:ring-gray-500'
        default:
          return 'bg-blue-800 text-white hover:bg-blue-900 focus:ring-blue-500'
      }
    }
  }, [variant, outline])

  // Clases base - minimalista
  const baseClasses = useMemo(() => {
    return `inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed ${sizeClasses} ${variantClasses} ${fullWidth ? 'w-full' : ''} ${className}`
  }, [sizeClasses, variantClasses, fullWidth, className])

  // Clases para iconos
  const iconSizeClasses = useMemo(() => {
    if (iconClassName) return iconClassName
    switch (size) {
      case 'sm':
        return 'h-4 w-4'
      case 'lg':
        return 'h-5 w-5'
      default:
        return 'h-4 w-4'
    }
  }, [size, iconClassName])

  return (
    <button
      disabled={isDisabled}
      className={baseClasses}
      {...rest}
    >
      {isLoading ? (
        <>
          <div className={`animate-spin rounded-full border-2 border-current border-t-transparent ${iconSizeClasses}`} />
          {children ? <span>{children}</span> : <span>Cargando...</span>}
        </>
      ) : (
        <>
          {LeftIcon && <LeftIcon className={iconSizeClasses} />}
          {children && <span>{children}</span>}
          {RightIcon && <RightIcon className={iconSizeClasses} />}
        </>
      )}
    </button>
  )
}

export default React.memo(Button)

